import React, { useState } from 'react';
import VoiceRecorder from '../voice/VoiceRecorder';
import { useAIGeneration } from './useAIGeneration';
import { GeneratedTemplate, PromptContext } from './promptTemplates';

export interface AITemplateGeneratorProps {
  onTemplateGenerated: (template: GeneratedTemplate) => void;
  onCancel: () => void;
  initialContext?: Partial<PromptContext>;
  className?: string;
}

const AITemplateGenerator: React.FC<AITemplateGeneratorProps> = ({
  onTemplateGenerated,
  onCancel,
  initialContext = {},
  className = ''
}) => {
  const [textInput, setTextInput] = useState('');
  const [context, setContext] = useState<Partial<PromptContext>>(initialContext);
  
  const { state, generateFromVoice, generateFromText, reset } = useAIGeneration();

  // Handle voice recording completion
  const handleVoiceRecording = async (audioBlob: Blob) => {
    try {
      const template = await generateFromVoice(audioBlob, context);
      onTemplateGenerated(template);
    } catch (error) {
      console.error('Voice generation failed:', error);
    }
  };

  // Handle text generation
  const handleTextGeneration = async () => {
    if (!textInput.trim()) return;

    try {
      const template = await generateFromText(textInput, context);
      onTemplateGenerated(template);
    } catch (error) {
      console.error('Text generation failed:', error);
    }
  };

  // Handle context changes
  const updateContext = (field: keyof PromptContext, value: string) => {
    setContext(prev => ({ ...prev, [field]: value }));
  };

  // Reset everything
  const handleReset = () => {
    reset();
    setTextInput('');
    setContext(initialContext);
  };

  // Progress indicator
  const getProgressStep = () => {
    switch (state.progress) {
      case 'transcribing': return 1;
      case 'generating': return 2;
      case 'complete': return 3;
      case 'error': return -1;
      default: return 0;
    }
  };

  const progressStep = getProgressStep();

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Progress Indicator */}
      {progressStep > 0 && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span>Progress:</span>
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-500 ${
                  progressStep === -1 ? 'bg-red-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.max(0, progressStep * 33.33)}%` }}
              />
            </div>
            <span className={progressStep === -1 ? 'text-red-600' : 'text-blue-600'}>
              {state.progress === 'transcribing' && 'Converting speech...'}
              {state.progress === 'generating' && 'Generating template...'}
              {state.progress === 'complete' && 'Complete!'}
              {state.progress === 'error' && 'Error occurred'}
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {/* Error Display */}
        {state.error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center space-x-2 text-red-700">
              <span>⚠️</span>
              <span className="text-sm font-medium">Generation Error</span>
            </div>
            <p className="text-sm text-red-600 mt-1">{state.error}</p>
            <button
              onClick={handleReset}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Direct Input Methods - Side by Side */}
        {!state.isTranscribing && !state.isGenerating && (
          <div className="space-y-4">
            {/* Input Methods Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Voice Input */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="text-xl">🎙️</div>
                  <h4 className="font-medium text-gray-900">Voice Input</h4>
                </div>
                <VoiceRecorder
                  onRecordingComplete={handleVoiceRecording}
                  onError={(error) => console.error('Recording error:', error)}
                  maxDuration={120}
                  disabled={state.isTranscribing || state.isGenerating}
                />
                {state.transcriptionResult && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                    <h5 className="text-sm font-medium text-blue-900 mb-2">Transcription:</h5>
                    <p className="text-sm text-blue-700">{state.transcriptionResult}</p>
                  </div>
                )}
              </div>

              {/* Text Input */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="text-xl">✍️</div>
                  <h4 className="font-medium text-gray-900">Text Input</h4>
                </div>
                <textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Describe the template you want to create...&#10;&#10;Example: Create a 401k consultation template for financial advisors to help clients understand retirement planning options, covering risk tolerance, investment timeline, and contribution strategies."
                  rows={6}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
                <button
                  onClick={handleTextGeneration}
                  disabled={!textInput.trim() || state.isGenerating}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {state.isGenerating ? 'Generating Template...' : 'Generate Template'}
                </button>
              </div>
            </div>

            {/* Context Settings */}
            <div className="border-t border-gray-200 pt-3">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Optional Context (helps improve results):</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <select
                    value={context.industry || ''}
                    onChange={(e) => updateContext('industry', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select industry</option>
                    <option value="financial_services">Financial Services</option>
                    <option value="real_estate">Real Estate</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="sales">Sales</option>
                    <option value="consulting">Consulting</option>
                    <option value="education">Education</option>
                    <option value="legal">Legal</option>
                    <option value="marketing">Marketing</option>
                    <option value="content">Content</option>
                    <option value="technology">Technology</option>
                  </select>
                </div>

                <div>
                  <select
                    value={context.difficulty || 'beginner'}
                    onChange={(e) => updateContext('difficulty', e.target.value as any)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="beginner">Standard</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                <div>
                  <input
                    type="text"
                    value={context.estimatedDuration || ''}
                    onChange={(e) => updateContext('estimatedDuration', e.target.value)}
                    placeholder="e.g., 30 minutes"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Generation Status */}
        {(state.isTranscribing || state.isGenerating) && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">
              {state.isTranscribing && 'Converting your speech to text...'}
              {state.isGenerating && 'AI is creating your template...'}
            </p>
            <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AITemplateGenerator; 